import type { CrmSetupMetaConnectionResult } from "../types";

export const META_EMBEDDED_SIGNUP_LOGIN_TIMEOUT_MS = 120_000;

export interface NormalizedEmbeddedSignupMessage {
  type?: string;
  event?: string;
  data?: {
    waba_id?: string;
    business_id?: string;
    whatsapp_business_accounts?: Array<{ id: string; name?: string }>;
    error_message?: string;
    errorMessage?: string;
  };
  waba_id?: string;
  business_id?: string;
  whatsapp_business_accounts?: Array<{ id: string; name?: string }>;
  error_message?: string;
  errorMessage?: string;
}

export function normalizeEmbeddedSignupMessage(
  raw: unknown,
): NormalizedEmbeddedSignupMessage | undefined {
  if (!raw) return undefined;

  let msg: unknown = raw;
  if (typeof raw === "string") {
    try {
      msg = JSON.parse(raw);
    } catch {
      return undefined;
    }
  }

  if (typeof msg === "object" && msg !== null && "payload" in msg) {
    const maybePayload = (msg as { payload?: unknown }).payload;
    if (
      maybePayload &&
      typeof maybePayload === "object" &&
      ("type" in maybePayload || "event" in maybePayload)
    ) {
      return maybePayload as NormalizedEmbeddedSignupMessage;
    }
  }

  return msg as NormalizedEmbeddedSignupMessage;
}

export function getIsSecureContextForFbLogin(): boolean {
  if (typeof window === "undefined") return false;
  const { protocol, hostname } = window.location;
  return (
    protocol === "https:" ||
    hostname === "localhost" ||
    hostname === "127.0.0.1"
  );
}

export function extractWabaFromEmbeddedSignupMessage(
  data: NormalizedEmbeddedSignupMessage,
): {
  wabaId: string | null;
  wabaName: string | null;
  businessId: string | null;
} {
  const wabaId =
    data.data?.waba_id ??
    data.waba_id ??
    data.data?.whatsapp_business_accounts?.[0]?.id ??
    data.whatsapp_business_accounts?.[0]?.id ??
    null;

  const wabaName =
    data.data?.whatsapp_business_accounts?.[0]?.name ??
    data.whatsapp_business_accounts?.[0]?.name ??
    null;

  const businessId = data.data?.business_id ?? data.business_id ?? null;

  return { wabaId, wabaName, businessId };
}

export function extractEmbeddedSignupErrorMessage(
  data: NormalizedEmbeddedSignupMessage,
): string | null {
  const raw =
    data.data?.error_message ??
    data.data?.errorMessage ??
    data.error_message ??
    data.errorMessage;
  const trimmed = typeof raw === "string" ? raw.trim() : "";
  return trimmed || null;
}

const EMBEDDED_SIGNUP_FINISH_EVENTS = [
  "FINISH",
  "FINISH_ONLY_WABA",
  "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING",
  "FINISH_OBO_MIGRATION",
  "FINISH_GRANT_ONLY_API_ACCESS",
] as const;

export function isEmbeddedSignupFinishEvent(event: string | undefined): boolean {
  if (!event) return false;
  return (EMBEDDED_SIGNUP_FINISH_EVENTS as readonly string[]).includes(event);
}

export function isEmbeddedSignupTerminalErrorEvent(
  event: string | undefined,
): boolean {
  return event === "CANCEL" || event === "ERROR";
}

export function metaConnectionSignature(
  r: CrmSetupMetaConnectionResult,
): string {
  return [
    r.wabaId ?? "",
    r.wabaName ?? "",
    r.authorizationCode ?? "",
    r.businessId ?? "",
  ].join("\u0001");
}

export function isSameMetaPayload(
  a: CrmSetupMetaConnectionResult | null,
  b: CrmSetupMetaConnectionResult,
): boolean {
  if (!a?.wabaId || !b.wabaId) return false;
  return metaConnectionSignature(a) === metaConnectionSignature(b);
}

export function mergeMetaConnectionResult(
  previous: CrmSetupMetaConnectionResult | null,
  partial: Partial<CrmSetupMetaConnectionResult>,
): CrmSetupMetaConnectionResult {
  return {
    authorizationCode:
      partial.authorizationCode !== undefined
        ? partial.authorizationCode
        : (previous?.authorizationCode ?? null),
    wabaId: partial.wabaId !== undefined ? partial.wabaId : (previous?.wabaId ?? null),
    businessId:
      partial.businessId !== undefined
        ? partial.businessId
        : (previous?.businessId ?? null),
    wabaName:
      partial.wabaName !== undefined ? partial.wabaName : (previous?.wabaName ?? null),
    primaryPhoneNumberId:
      partial.primaryPhoneNumberId !== undefined
        ? partial.primaryPhoneNumberId
        : previous?.primaryPhoneNumberId,
    primaryPhoneValue:
      partial.primaryPhoneValue !== undefined
        ? partial.primaryPhoneValue
        : previous?.primaryPhoneValue,
  };
}

export function resolveEmbeddedSignupConfigurationError(params: {
  appId?: string;
  configId?: string;
}): string | null {
  if (!params.appId?.trim()) {
    return "VITE_META_APP_ID não configurado.";
  }
  if (!params.configId?.trim()) {
    return "VITE_META_EMBEDDED_SIGNUP_CONFIG_ID não configurado.";
  }
  return null;
}
